<template>
  <tr class="data-col">
    <td class="survey-badges-col">
      <template v-if="isNew">
        <span class="badge badge-blue">
          <i class="fa fa-star" aria-hidden="true"></i> Nouveau
        </span>
        <br />
      </template>
      <small class="namespace" v-if="namespace">
        {{ $t("survey." + namespace) }}
      </small>
    </td>

    <td>
      <template v-if="startedAt">
        <strong class="only-sm">{{ $t("survey.startedAt") }}: </strong>
        {{ convertDate(startedAt) }}
      </template>
      <template v-else-if="updatedAt">
        <strong class="only-sm">{{ $t("survey.startedAt") }}: </strong>
        {{ convertDate(updatedAt) }}
      </template>
      <template v-else>-</template>
    </td>
    <td>
      <strong class="only-sm">{{ $t("survey.updatedAt") }}: </strong>
      <template v-if="updatedAt">
        {{ convertDate(updatedAt) }}
      </template>
      <template v-else>-</template>
    </td>
    <td class="status-col">
      <strong class="only-sm">{{ $t("survey.statusTitle") }}: </strong>
      <span
        class="badge"
        :class="{
          inProgress: isInProgress,
          completed: isCompleted,
          notStarted: isNotStarted,
        }"
        >{{ $t("survey.status." + status) }}</span
      >
    </td>
    <td class="username-overflow" :title="username">
      <strong class="only-sm">{{ $t("survey.username") }}: </strong>
      <template v-if="userId && currentUser">
        <template v-if="currentUser.id === userId">
          <span class="badge badge-blue">
            <i class="fa fa-user" aria-hidden="true"></i>
          </span>
          <span class="font-weight-bold current-user">
              {{ username }}
            <small>({{ $t("survey.me") }})</small>
          </span>
        </template>
        <template v-else>
          {{ username }}
        </template>
      </template>
      <template v-else>-</template>
    </td>
    <td class="actions">
      <Dropdown v-if="!serviceUnavailable && showAction" :disabled="disabled">
        <DropdownToggler slot="toggler" text="Actions" :disabled="disabled" />
        <DropdownContent>
          <DropdownItem
            @clicked="$emit('viewResult')"
            v-if="isCompleted && !archived"
          >
            {{ $t("survey.gotoResult") }}
          </DropdownItem>
          <DropdownItem
            @clicked="$emit('viewPDFResult')"
            v-if="isCompleted && archived"
          >
            {{ $t("survey.download") }}
          </DropdownItem>
          <DropdownItem
            @clicked="$emit('continueSurvey')"
            v-if="editable"
            :disabled="isCompleted && !completedCanBeEdited"
          >
            <template v-if="isInProgress">{{
              $t("survey.continueSurvey")
            }}</template>
            <template v-else-if="isCompleted">{{
              $t("survey.modifySurvey")
            }}</template>
            <template v-else-if="isNotStarted">{{
              $t("survey.startSurvey")
            }}</template>
          </DropdownItem>
          <DropdownItem @clicked="$emit('editData')" v-if="editable">
            {{ $t("survey.modifyData") }}
          </DropdownItem>
          <DropdownItem
            @clicked="$emit('shareData')"
            v-if="editable || archived"
          >
            {{ $t("survey.shareData") }}
          </DropdownItem>
          <DropdownItem
            @clicked="$emit('compareData')"
            v-if="isCompleted && !archived"
          >
            {{ $t("survey.compareData") }}
          </DropdownItem>
          <!-- <hr> -->
        </DropdownContent>
      </Dropdown>
      <div v-else-if="serviceUnavailable" class="serviceUnavailable">
        <strong
          >Le questionnaire est temporairement indisponible, veuillez réessayer
          plus tard.</strong
        >
      </div>
      <div v-else-if="archived" class="serviceUnavailable">
        <strong
          >Cette version du questionnaire n'est plus disponible. Merci de créer
          un nouveau diagnostic.</strong
        >
      </div>
    </td>
  </tr>
</template>

<script>
import {
  format, addDays, parseISO, isAfter,
} from 'date-fns';
import Dropdown from '@/components/Dropdown/Dropdown.vue';
import DropdownContent from '@/components/Dropdown/DropdownContent.vue';
import DropdownItem from '@/components/Dropdown/DropdownItem.vue';
import DropdownToggler from '@/components/Dropdown/DropdownToggler.vue';
import { getInstance } from '@/auth/authWrapper';

export default {
  name: 'SurveyEntry',
  props: {
    id: String,
    namespace: String,
    startedAt: String,
    updatedAt: String,
    lastLoginAt: String,
    status: String,
    username: String,
    userId: String,
    editable: Boolean,
    archived: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
    serviceUnavailable: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownToggler,
  },
  methods: {
    convertDate(d) {
      let date;
      if (d) {
        date = format(new Date(d), 'dd/MM/yyyy HH:mm');
        return date;
      }
      return date;
    },
  },
  data() {
    return {
      currentUser: null,
    };
  },
  mounted() {
    const authService = getInstance();
    this.currentUser = authService.user;
  },
  computed: {
    isNew() {
      return isAfter(parseISO(this.updatedAt), parseISO(this.lastLoginAt));
    },
    isNotStarted() {
      return this.status === 'NotStarted';
    },
    isInProgress() {
      return this.status === 'InProgress';
    },
    isCompleted() {
      return this.status === 'Completed';
    },
    completedCanBeEdited() {
      const startedDate = this.completedDate ? this.completedDate : this.updatedAt;
      return addDays(new Date(startedDate), 15) > new Date();
    },
    showAction() {
      return this.editable || this.isCompleted;
    },
  },
};
</script>

<style>
.username-overflow {
  overflow: hidden;
  max-width: 200px;
  text-overflow: ellipsis;
}
.actions {
  position: relative;
  min-width: 175px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.actions button {
  max-width: 150px;
}
.badge {
  border-radius: 4px;
  padding: 2px 6px;
  background-color: #999;
  color: white;
  font-weight: bold;
  font-size: 12px;
}
.badge.inProgress,
.badge.badge-blue {
  background-color: #0d93d2;
}
.badge.completed {
  background-color: #699e24;
}
.badge.notStarted {
  background-color: #736994;
}
.serviceUnavailable {
  padding: 0 1em;
  text-align: right;
}

.survey-list-table td {
  width: calc(100% / 6);
  vertical-align: middle;
}
.status-col {
  text-align: center;
}
.only-sm {
  display: none;
}

@media only screen and (max-width: 768px) {
  .survey-list-table tr {
    display: none;
  }
  .data-col {
    display: flex !important;
    flex-direction: column;
    border-top: 1px solid #eee;
  }
  .survey-list-table td {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
  }
  strong {
    padding-right: 0.5em;
  }
  .status-col {
    text-align: center;
  }
  .only-sm {
    display: block;
  }
  .survey-badges-col {
    padding-top: 1em;
  }
}
</style>

<style scoped>
small.namespace {
  font-size: 12px;
}
.font-weight-bold {
  font-weight: bold;
}
.capitalize {
  text-transform: capitalize;
}
.current-user small {
  font-size: 10px;
  font-weight: normal;
  text-transform: none;
}
</style>
